import React, { useMemo, useState } from "react";

function classify(qKey, dataGroup, variableTypes) {
  const q = dataGroup[qKey];
  const names = (q?.varName || []).map((v) => v.Name).filter(Boolean);
  if (!names.length) return null;
  let role = "categorical";
  if (variableTypes) {
    role = names.every((n) => variableTypes.numeric?.includes(n)) ? "numeric" : "categorical";
  } else {
    const t = (q?.type || "").toLowerCase();
    role = t === "numeric" || t.includes("numeric") ? "numeric" : "categorical";
  }
  return { key: qKey, role, names, single: names.length === 1, label: q?.Qtitle || qKey };
}

/**
 * The Dataset Variables browser: every variable in the project, searchable and
 * filterable by type, each row showing whether it's assigned to the currently
 * "active" role zone (a checkmark) or can be added to it (a + button).
 */
function DatasetVariablesPanel({ dataGroup, variableTypes, activeZone, assignedKeys, onToggle }) {
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");

  const rows = useMemo(() => {
    return Object.keys(dataGroup || {})
      .map((key) => classify(key, dataGroup, variableTypes))
      .filter(Boolean)
      .filter((r) => (filter === "all" ? true : r.role === filter))
      .filter((r) => !search || r.label.toLowerCase().includes(search.toLowerCase()) || r.key.toLowerCase().includes(search.toLowerCase()));
  }, [dataGroup, variableTypes, filter, search]);

  const assigned = new Set(assignedKeys || []);

  return (
    <div className="dv-panel">
      <div className="dv-panel-header">
        <h4>Dataset Variables</h4>
        <span className="dv-count">{rows.length} shown</span>
      </div>
      <input
        className="dv-search"
        type="text"
        placeholder="Search variables..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <div className="dv-filter-chips">
        {["all", "numeric", "categorical"].map((f) => (
          <button
            key={f}
            className={`dv-chip ${filter === f ? "active" : ""}`}
            onClick={() => setFilter(f)}
          >
            {f === "all" ? "All" : f === "numeric" ? "Numeric" : "Categorical"}
          </button>
        ))}
      </div>

      {activeZone && (
        <div className="dv-active-zone-hint">
          Assigning to: <strong>{activeZone.label}</strong>
          {activeZone.role !== "any" && ` (${activeZone.role} only)`}
        </div>
      )}

      <div className="dv-list">
        {rows.map((r) => {
          const isAssigned = assigned.has(r.key);
          const compatible = !activeZone || activeZone.role === "any" || activeZone.role === r.role;
          const requiresSingle = activeZone && !activeZone.multiple && !r.single;
          const clickable = !!activeZone && compatible && !requiresSingle;
          return (
            <div
              key={r.key}
              className={`dv-row ${isAssigned ? "assigned" : ""} ${clickable ? "clickable" : "disabled"}`}
              onClick={() => clickable && onToggle(r.key)}
              title={requiresSingle ? "This role needs a single-variable question" : undefined}
            >
              <span className={`dv-badge dv-badge-${r.role}`}>{r.role === "numeric" ? "N" : "C"}</span>
              <span className="dv-info">
                <span className="dv-label">{r.label}</span>
                <span className="dv-code">{r.key}</span>
              </span>
              <span className={`dv-indicator ${isAssigned ? "check" : "plus"}`}>
                {isAssigned ? "✓" : clickable ? "+" : ""}
              </span>
            </div>
          );
        })}
        {rows.length === 0 && <p className="analysis-muted">No variables match.</p>}
      </div>
    </div>
  );
}

export default DatasetVariablesPanel;
